import { Button, Checkbox, Field, Input, Select, Textarea } from '@fluentui/react-components';
import type { FormField, FormResponses } from '@qcbot/shared';

interface Props {
  field: FormField;
  value: unknown;
  onChange: (fieldKey: string, value: unknown) => void;
}

/** Renders one form-builder field by its type — the add-in-side counterpart to the admin
 * form-builder UI. Keeps values in a plain FormResponses object keyed by fieldKey. */
export function DynamicFormField({ field, value, onChange }: Props) {
  const label = field.required ? `${field.label} *` : field.label;

  switch (field.fieldType) {
    case 'textarea':
      return (
        <Field label={label} hint={field.helpText ?? undefined}>
          <Textarea
            value={(value as string) ?? ''}
            placeholder={field.placeholder ?? undefined}
            onChange={(_, d) => onChange(field.fieldKey, d.value)}
          />
        </Field>
      );
    case 'number':
      return (
        <Field label={label} hint={field.helpText ?? undefined}>
          <Input
            type="number"
            value={value != null ? String(value) : ''}
            placeholder={field.placeholder ?? undefined}
            onChange={(_, d) => onChange(field.fieldKey, d.value ? Number(d.value) : undefined)}
          />
        </Field>
      );
    case 'date':
      return (
        <Field label={label} hint={field.helpText ?? undefined}>
          <Input type="date" value={(value as string) ?? ''} onChange={(_, d) => onChange(field.fieldKey, d.value)} />
        </Field>
      );
    case 'checkbox':
      return (
        <Checkbox
          label={label}
          checked={Boolean(value)}
          onChange={(_, d) => onChange(field.fieldKey, Boolean(d.checked))}
        />
      );
    case 'select':
      return (
        <Field label={label} hint={field.helpText ?? undefined}>
          <Select value={(value as string) ?? ''} onChange={(_, d) => onChange(field.fieldKey, d.value)}>
            <option value="" disabled>
              Select…
            </option>
            {(field.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
      );
    case 'multiselect': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <Field label={label} hint={field.helpText ?? undefined}>
          <Select
            multiple
            value={selected}
            onChange={(e) => onChange(field.fieldKey, Array.from(e.target.selectedOptions).map((o) => o.value))}
          >
            {(field.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
      );
    }
    case 'text':
    default:
      return (
        <Field label={label} hint={field.helpText ?? undefined}>
          <Input
            value={(value as string) ?? ''}
            placeholder={field.placeholder ?? undefined}
            onChange={(_, d) => onChange(field.fieldKey, d.value)}
          />
        </Field>
      );
    case 'image': {
      const dataUrl = typeof value === 'string' ? value : undefined;
      function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-selecting the same file later
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => onChange(field.fieldKey, reader.result);
        reader.readAsDataURL(file);
      }
      return (
        <Field label={label} hint={field.helpText ?? undefined}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {dataUrl && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img
                  src={dataUrl}
                  alt={field.label}
                  style={{ maxHeight: 48, maxWidth: 120, border: '1px solid #e0e0e0', borderRadius: 4, objectFit: 'contain' }}
                />
                <Button size="small" appearance="subtle" onClick={() => onChange(field.fieldKey, undefined)}>
                  Remove
                </Button>
              </div>
            )}
            <input type="file" accept="image/*" onChange={handleFile} />
          </div>
        </Field>
      );
    }
  }
}

/** Applies each field's defaultValue for keys not already present in `responses`. */
export function applyFormDefaults(fields: FormField[], responses: FormResponses): FormResponses {
  const next = { ...responses };
  for (const field of fields) {
    if (!(field.fieldKey in next) && field.defaultValue !== undefined) next[field.fieldKey] = field.defaultValue;
  }
  return next;
}

/** True if every required field has a non-empty value. */
export function isFormValid(fields: FormField[], responses: FormResponses): boolean {
  return fields.every((f) => {
    if (!f.required) return true;
    const v = responses[f.fieldKey];
    return v !== undefined && v !== null && v !== '';
  });
}
